'use client';

import Box from '@enlighten/design-system/dist/Box';
import Stack from '@enlighten/design-system/dist/Stack';
import Typography from '@enlighten/design-system/dist/Typography';
import React, { useState } from 'react';
import { useInView } from 'react-intersection-observer';

interface TitleSegment {
	text: string;
	highlighted?: boolean;
}

interface Reason {
	tab: string;
	title: TitleSegment[];
	description: string;
}

const REASONS: Reason[] = [
	{
		tab: '조건 비교도',
		title: [{ text: '같은 발전소라도 ' }, { text: '매입처에 따라 조건', highlighted: true }, { text: '이 크게 ' }, { text: '달라집니다.', highlighted: true }],
		description: '한 곳만 보고 결정하면 더 나은 조건을 놓칠 수 있습니다.',
	},
	{
		tab: '안 된다던 곳도',
		title: [{ text: '토지 소유 형태나 계통 상태에 따라', highlighted: true }, { text: ' 매각 난이도가 다릅니다.' }],
		description: '임대부지, 종중부지, 계통 미연계 발전소도 대응 가능한지 먼저 확인합니다.',
	},
	{
		tab: '실사비용도',
		title: [{ text: '조회와 실사', highlighted: true }, { text: '에 드는 ' }, { text: '비용', highlighted: true }, { text: '은 ' }, { text: '전액 지원', highlighted: true }, { text: '합니다.' }],
		description: '매입가 조회는 무료이며, 실사비용도 부담 없이 진행할 수 있습니다.',
	},
	{
		tab: '잔금 공백도',
		title: [{ text: '잔금 시점의 자금 공백까지', highlighted: true }, { text: ' 함께 설계합니다.' }],
		description: '필요 시 토지잔금 대출을 연계해 일정에 맞춰 진행합니다.',
	},
];

const CONTENT_MAX_WIDTH = 920;
const CONTENT_PADDING_X = 24;
const TAB_GAP = 31;
const PROGRESS_DURATION = 4000;

const CARD_TITLE_COLOR = '#36383A';

const HighlightedTitle = ({ title }: { title: TitleSegment[] }) => (
	<>
		{title.map(({ text, highlighted }) => (
			<Box key={text} component="span" color={highlighted ? 'primary.main' : 'inherit'}>
				{text}
			</Box>
		))}
	</>
);

const ReasonCard = ({ title, description }: Reason) => (
	<Stack gap="5px" padding="19px" bgcolor="common.white" border="1px solid" borderColor="grey.300" borderRadius="14px">
		<Typography fontSize="16px" fontWeight="bold" lineHeight={1.4} letterSpacing="-0.32px" color={CARD_TITLE_COLOR} sx={{ wordBreak: 'keep-all' }}>
			<HighlightedTitle title={title} />
		</Typography>
		<Typography fontSize="16px" lineHeight={1.4} color="grey.900" sx={{ wordBreak: 'keep-all' }}>
			{description}
		</Typography>
	</Stack>
);

const Section3Reasons = () => {
	const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.5 });
	const [activeIndex, setActiveIndex] = useState(0);

	const activeReason = REASONS[activeIndex];

	return (
		<>
			<Stack display={{ xs: 'flex', md: 'none' }} gap="16px" paddingX="24px">
				{REASONS.map((reason) => (
					<ReasonCard key={reason.tab} {...reason} />
				))}
			</Stack>

			<Stack ref={ref} display={{ xs: 'none', md: 'flex' }} width="100%" alignItems="center" gap="40px">
				<Box display="grid" width="100%" maxWidth={`${CONTENT_MAX_WIDTH}px`} paddingX={`${CONTENT_PADDING_X}px`} gridTemplateColumns={`repeat(${REASONS.length}, 1fr)`} columnGap={`${TAB_GAP}px`}>
					{REASONS.map((reason, index) => (
						<Stack key={reason.tab} component="button" type="button" onClick={() => setActiveIndex(index)} sx={{ padding: 0, border: 'none', background: 'none', cursor: 'pointer' }}>
							<Typography fontSize="28px" fontWeight="bold" lineHeight={1.4} textAlign="center" color={index === activeIndex ? 'primary.main' : 'grey.600'} sx={{ wordBreak: 'keep-all' }}>
								{reason.tab}
							</Typography>

							{/* 한 탭만 두 줄이 돼도 게이지 높이는 칸마다 어긋나지 않게 바닥에 붙인다 */}
							<Box height="3px" marginTop="auto">
								{/* 활성 칸에서만 새로 마운트되므로 게이지가 매번 0 부터 다시 찬다. 다 찬 시점(animationend)이 곧 다음 탭 전환 */}
								{index === activeIndex && (
									<Box
										height="100%"
										bgcolor="primary.main"
										onAnimationEnd={() => setActiveIndex((prev) => (prev + 1) % REASONS.length)}
										sx={{
											'@keyframes site-sale-reason-progress': {
												from: { transform: 'scaleX(0)' },
												to: { transform: 'scaleX(1)' },
											},
											transformOrigin: 'left',
											animation: `site-sale-reason-progress ${PROGRESS_DURATION}ms linear forwards`,
											animationPlayState: inView ? 'running' : 'paused',
											'@media (prefers-reduced-motion: reduce)': { animation: 'none' },
										}}
									/>
								)}
							</Box>
						</Stack>
					))}
				</Box>

				<Box width="100%" maxWidth={`${CONTENT_MAX_WIDTH}px`} paddingX={`${CONTENT_PADDING_X}px`}>
					<Stack justifyContent="center" gap="8px" width="100%" height="140px" paddingX="40px" bgcolor="common.white" borderRadius="20px" boxShadow="0 4px 10px 0 rgba(0, 0, 0, 0.05)">
						<Typography fontSize="28px" fontWeight="bold" lineHeight={1.4} color={CARD_TITLE_COLOR} whiteSpace="nowrap">
							<HighlightedTitle title={activeReason.title} />
						</Typography>
						<Typography fontSize="18px" lineHeight={1.4} color="grey.900" whiteSpace="nowrap">
							{activeReason.description}
						</Typography>
					</Stack>
				</Box>
			</Stack>
		</>
	);
};

export default Section3Reasons;
